/**
 * Entity Data Processing — For-API Submission
 *
 * Main processor that prepares an in-memory entity for the API:
 * field-type normalization, "me" resolution, DECIMAL stringification,
 * and stripping of relation data.
 */

import type {
  BaseEntity,
  SimpleFieldConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";
import {
  filterRelationData,
  processTagsField,
  resolveUserIds,
} from "./helpers";
import { ALL_BIGFLOAT_FIELDS, ALL_BIGINT_FIELDS } from "@/lib/graphql/serialize-input";
import { logger } from "../../../logger";

// ============================================================================
// MAIN PROCESSING FUNCTION
// ============================================================================

/**
 * Process task data for API submission
 * This is the main function that handles all data processing before sending to the API
 */
export const processEntityDataForApi = <T extends BaseEntity>(
  item: T,
  fieldsConfig: Record<string, SimpleFieldConfig>,
  currentUserId?: string
): T => {
  // Work against a mutable string-keyed view: this processor rewrites field
  // values to their API-serialized shape (arrays → tags, "me" → user id,
  // numbers, JSON strings), which does not fit the original `T[keyof T]` types.
  const processedItem = { ...item } as Record<string, unknown>;

  // First pass: Process each field based on its type, but exclude fields marked with excludeFromApi
  Object.keys(fieldsConfig).forEach((fieldId) => {
    const fieldConfig = fieldsConfig[fieldId];

    // Skip fields that should be excluded from API
    if (fieldConfig.excludeFromApi === true) {
      delete processedItem[fieldId];
      return;
    }

    const fieldValue = processedItem[fieldId];

    switch (fieldConfig.type) {
      case "tags":
        // Convert comma-separated string to array for tags
        if (typeof fieldValue === "string") {
          processedItem[fieldId] = processTagsField(fieldValue);
        }
        break;

      case "multi-user":
        // Resolve "me" values to actual user IDs for multi-user fields
        if (Array.isArray(fieldValue)) {
          const userIds = fieldValue as string[];
          processedItem[fieldId] = resolveUserIds(userIds, currentUserId);
        } else if (fieldValue) {
          // Handle case where single value is passed instead of array
          const singleUserId = fieldValue === "me" && currentUserId ? currentUserId : fieldValue;
          processedItem[fieldId] = [singleUserId];
        }
        break;

      case "user":
        // Resolve "me" value for single user fields
        if (fieldValue === "me" && currentUserId) {
          processedItem[fieldId] = currentUserId;
        }
        break;

      case "number":
        // Convert string numbers to actual numbers
        if (fieldValue !== null && fieldValue !== undefined) {
          const numValue = Number(fieldValue);
          if (!isNaN(numValue)) {
            processedItem[fieldId] = numValue;
          }
        }
        break;

      case "json": {
        // Supabase GraphQL / Postgres jsonb: empty string is invalid; omit or send valid JSON strings
        const v = fieldValue;
        if (v === null || v === undefined) {
          delete processedItem[fieldId];
          break;
        }
        if (typeof v === "string") {
          const trimmed = v.trim();
          if (trimmed === "") {
            delete processedItem[fieldId];
            break;
          }
          try {
            JSON.parse(trimmed);
            processedItem[fieldId] = trimmed;
          } catch {
            logger.warn(`Invalid JSON in field ${fieldId}`, { fieldId, fieldValue });
            delete processedItem[fieldId];
          }
          break;
        }
        if (typeof v === "object") {
          processedItem[fieldId] = JSON.stringify(v);
        }
        break;
      }

      // Add more field types as needed
      default:
        // No processing needed for other field types
        break;
    }
  });

  // Second pass: Handle DECIMAL/BigFloat fields that need to be serialized as strings for GraphQL
  // and ensure any numeric fields that weren't in fieldsConfig are still converted appropriately.
  // Uses the canonical BigFloat field registry so every entity's decimal columns
  // (deals, companies, time_entries, etc.) are covered — not just deal fields.
  const numericFieldNames = ["close_probability"];

  // BigFloat (DECIMAL) and BigInt (int8) both serialize to strings for pg_graphql.
  const stringScalarFields = [...ALL_BIGFLOAT_FIELDS, ...ALL_BIGINT_FIELDS];
  stringScalarFields.forEach((fieldName) => {
    if (fieldName in processedItem) {
      const value = processedItem[fieldName];
      if (value !== null && value !== undefined && typeof value !== "string") {
        processedItem[fieldName] = String(value);
      }
    }
  });

  numericFieldNames.forEach((fieldName) => {
    if (fieldName in processedItem) {
      const value = processedItem[fieldName];
      if (value !== null && value !== undefined && typeof value !== "number") {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          processedItem[fieldName] = numValue;
        }
      }
    }
  });

  // Filter out relation data that shouldn't be sent to the API
  return filterRelationData(processedItem as T);
};

// Backward compatibility alias (to be removed after migration)
export const processTaskDataForApi = processEntityDataForApi;
